// SeatCard — player profile block shown above / below the board.
// Avatar + name + wallet + live digital clock + disconnect indicator
// + pulsing "YOUR TURN" / "THEIR TURN" badge so whose-turn-it-is is
// obvious at a glance.
//
// The clock ticks locally while `isActive` is true; parent re-syncs
// `clockMs` from the server on every move so drift stays tight. We
// render mm:ss above 10s and mm:ss.T (with tenths) under 10s — same
// convention lichess uses. When the clock dips under 30 s we flip a
// `.chess-seatcard--low` class so the CSS can animate a red pulse.

import { useEffect, useRef, useState } from 'react';

type Props = {
  name: string;
  avatarUrl?: string | null;
  isBot?: boolean;
  /** Bot difficulty tier from `bots.personality_type` — 'easy' |
   *  'medium' | 'hard'. Rendered as a colour-coded tag next to the
   *  name when isBot is true. Same UX Carrom uses so the player
   *  can confirm matchmaking handed them the difficulty they
   *  expected. Pure cosmetic; the engine doesn't read this. */
  botMode?: string | null;
  /** Wallet balance shown next to the name — shown on both sides so
   *  each player can see how much is riding on the other's bet. */
  walletBalance?: number | null;
  /** Full remaining-millis from the server. Caller updates this on
   *  each move broadcast. */
  clockMs: number;
  /** Is this side currently on-move? Enables the local 100ms tick. */
  isActive: boolean;
  /** Does this card belong to the viewer ("me") or the opponent?
   *  Drives the "YOUR TURN" vs "THEIR TURN" label on the badge. */
  isMe: boolean;
  /** Layout hint — 'top' renders with avatar on the left + clock
   *  right; 'bottom' is the same pattern (we don't mirror — keeps
   *  layouts consistent whether you're white or black). */
  side: 'top' | 'bottom';
  /** True when the opposite side's WS is disconnected — greys out +
   *  shows the "Reconnecting…" label. */
  disconnected?: boolean;
};

function fmtClock(ms: number): string {
  const safe = Math.max(0, ms | 0);
  const totalSec = Math.floor(safe / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (safe < 10_000) {
    const tenths = Math.floor((safe % 1000) / 100);
    return `${m}:${String(s).padStart(2, '0')}.${tenths}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtMoney(n: number | null | undefined): string {
  if (n == null) return '';
  return n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

export default function SeatCard({
  name, avatarUrl, isBot, botMode, walletBalance,
  clockMs, isActive, isMe, side, disconnected,
}: Props) {
  const [displayMs, setDisplayMs] = useState(clockMs);
  const lastSyncRef = useRef({ value: clockMs, ts: Date.now() });

  useEffect(() => {
    lastSyncRef.current = { value: clockMs, ts: Date.now() };
    setDisplayMs(clockMs);
  }, [clockMs]);

  useEffect(() => {
    if (!isActive) {
      setDisplayMs(clockMs);
      return;
    }
    const tick = () => {
      const { value, ts } = lastSyncRef.current;
      setDisplayMs(Math.max(0, value - (Date.now() - ts)));
    };
    tick();
    const id = window.setInterval(tick, 100);
    return () => window.clearInterval(id);
  }, [isActive, clockMs]);

  const low = displayMs < 30_000 && isActive;

  return (
    <div
      className={[
        'chess-seatcard',
        `chess-seatcard--${side}`,
        isActive ? 'chess-seatcard--active' : '',
        low ? 'chess-seatcard--low' : '',
        disconnected ? 'chess-seatcard--disconnected' : '',
      ].filter(Boolean).join(' ')}
    >
      <div className="chess-seatcard__left">
        <div className="chess-seatcard__avatar">
          {avatarUrl
            ? <img src={avatarUrl} alt="" />
            : (
              <span className="chess-seatcard__initial">
                {(name || '?').slice(0, 1).toUpperCase()}
              </span>
            )}
        </div>
        <div className="chess-seatcard__ident">
          <div className="chess-seatcard__name">
            {/* Platform-wide rule: never expose to the player that
                they're playing against a bot. We render the bot's
                display name only — no " · BOT" suffix, no
                EASY/MEDIUM/HARD pill. The `isBot` and `botMode`
                props are still accepted (so the engine state can
                drive other behaviour) but produce no visible
                output here. */}
            {name || 'Player'}
          </div>
          {walletBalance != null && (
            <div className="chess-seatcard__wallet">
              <img
                src="/games/chess/Coin.png"
                alt=""
                className="chess-seatcard__coin"
                draggable={false}
              />
              <span>{fmtMoney(walletBalance)}</span>
            </div>
          )}
          {disconnected && (
            <div className="chess-seatcard__status">Reconnecting…</div>
          )}
        </div>
      </div>
      <div className="chess-seatcard__clock" aria-label="Time remaining">
        {fmtClock(displayMs)}
      </div>
      {/* Turn badge is ABSOLUTELY positioned (see `.chess-seatcard__
          turn` rule) so showing / hiding it doesn't change the
          SeatCard's height — which earlier was causing the whole
          board to jitter up and down whenever the turn flipped. It
          floats above the top edge of the card, peeking out, and
          takes ZERO layout space. */}
      {isActive && (
        <div
          className={[
            'chess-seatcard__turn',
            isMe ? 'chess-seatcard__turn--mine' : 'chess-seatcard__turn--theirs',
          ].join(' ')}
          aria-label={isMe ? 'Your turn' : "Opponent's turn"}
        >
          {isMe ? 'YOUR TURN' : 'THINKING…'}
        </div>
      )}
    </div>
  );
}
